import { useQuery } from '@tanstack/react-query';
import { apiHelpers } from '../../services/api';
import ReactECharts from 'echarts-for-react';
import {
  UserGroupIcon,
  CircleStackIcon,
  SparklesIcon,
  ChartBarIcon
} from '@heroicons/react/24/outline';

export default function BillingUsage() {
  const { data: usageData, isLoading: usageLoading } = useQuery({
    queryKey: ['billingUsage'],
    queryFn: () => apiHelpers.getUsage()
  });
  
  const { data: storageData } = useQuery({
    queryKey: ['storageUsage'],
    queryFn: () => apiHelpers.getStorageUsage()
  });
  
  const { data: aiData } = useQuery({
    queryKey: ['aiUsage'],
    queryFn: () => apiHelpers.getAITokenUsage()
  });
  
  const usage = usageData?.data;
  const storage = storageData?.data;
  const ai = aiData?.data;
  
  // AI usage chart options
  const aiChartOptions = {
    tooltip: { trigger: 'axis' },
    xAxis: {
      type: 'category',
      data: ai?.dailyUsage?.map(d => new Date(d.date).toLocaleDateString()) || []
    },
    yAxis: { type: 'value' },
    series: [
      {
        name: 'Tokens Used',
        type: 'bar',
        data: ai?.dailyUsage?.map(d => d.total) || [],
        itemStyle: { color: '#3b82f6' }
      }
    ],
    grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true }
  };
  
  if (usageLoading) {
    return (
      <div className="animate-fade-in">
        <div className="skeleton h-8 w-48 mb-8" />
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          {[1, 2, 3].map(i => (
            <div key={i} className="card p-6">
              <div className="skeleton h-20" />
            </div>
          ))}
        </div>
      </div>
    );
  }
  
  return (
    <div className="animate-fade-in">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-2xl font-display font-bold text-surface-900 dark:text-white">
          Billing & Usage
        </h1>
        <p className="text-surface-500 dark:text-surface-400 mt-1">
          Monitor your resource consumption
        </p>
      </div>
      
      {/* Usage Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        {/* Seats */}
        <div className="card p-6">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-12 h-12 rounded-xl bg-primary-100 dark:bg-primary-900 flex items-center justify-center">
              <UserGroupIcon className="w-6 h-6 text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <p className="text-sm text-surface-500">Active Seats</p>
              <p className="text-2xl font-display font-bold text-surface-900 dark:text-white">
                {usage?.usage?.seats?.current || 0}
                <span className="text-sm font-normal text-surface-400"> / {usage?.usage?.seats?.limit || 5}</span>
              </p>
            </div>
          </div>
          <div className="w-full bg-surface-200 dark:bg-surface-700 rounded-full h-2">
            <div
              className="bg-primary-600 h-2 rounded-full transition-all"
              style={{ width: `${Math.min(usage?.usage?.seats?.percentage || 0, 100)}%` }}
            />
          </div>
          <p className="text-xs text-surface-500 mt-2">
            {usage?.usage?.seats?.percentage?.toFixed(1)}% used
          </p>
        </div>
        
        {/* Storage */}
        <div className="card p-6">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-12 h-12 rounded-xl bg-green-100 dark:bg-green-900 flex items-center justify-center">
              <CircleStackIcon className="w-6 h-6 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <p className="text-sm text-surface-500">Storage Used</p>
              <p className="text-2xl font-display font-bold text-surface-900 dark:text-white">
                {usage?.usage?.storage?.currentFormatted || '0 B'}
                <span className="text-sm font-normal text-surface-400"> / {usage?.usage?.storage?.limitFormatted || '10 GB'}</span>
              </p>
            </div>
          </div>
          <div className="w-full bg-surface-200 dark:bg-surface-700 rounded-full h-2">
            <div
              className="bg-green-600 h-2 rounded-full transition-all"
              style={{ width: `${Math.min(usage?.usage?.storage?.percentage || 0, 100)}%` }}
            />
          </div>
          <p className="text-xs text-surface-500 mt-2">
            {usage?.usage?.storage?.percentage?.toFixed(1)}% used
          </p>
        </div>
        
        {/* AI Tokens */}
        <div className="card p-6">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-12 h-12 rounded-xl bg-purple-100 dark:bg-purple-900 flex items-center justify-center">
              <SparklesIcon className="w-6 h-6 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <p className="text-sm text-surface-500">AI Tokens</p>
              <p className="text-2xl font-display font-bold text-surface-900 dark:text-white">
                {((usage?.usage?.aiTokens?.total || 0) / 1000).toFixed(1)}K
                <span className="text-sm font-normal text-surface-400"> / {((usage?.usage?.aiTokens?.limit || 100000) / 1000).toFixed(0)}K</span>
              </p>
            </div>
          </div>
          <div className="w-full bg-surface-200 dark:bg-surface-700 rounded-full h-2">
            <div
              className="bg-purple-600 h-2 rounded-full transition-all"
              style={{ width: `${Math.min(usage?.usage?.aiTokens?.percentage || 0, 100)}%` }}
            />
          </div>
          <p className="text-xs text-surface-500 mt-2">
            {usage?.usage?.aiTokens?.percentage?.toFixed(1)}% used this month
          </p>
        </div>
      </div>
      
      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* AI Usage Chart */}
        <div className="card">
          <div className="p-4 border-b border-surface-200 dark:border-surface-800">
            <h2 className="font-medium text-surface-900 dark:text-white flex items-center gap-2">
              <SparklesIcon className="w-5 h-5 text-purple-500" />
              AI Token Usage
            </h2>
          </div>
          <div className="p-4">
            {ai?.dailyUsage?.length > 0 ? (
              <ReactECharts option={aiChartOptions} style={{ height: 300 }} />
            ) : (
              <div className="h-[300px] flex items-center justify-center text-surface-500">
                No usage data yet
              </div>
            )}
          </div>
        </div>
        
        {/* Storage Breakdown */}
        <div className="card">
          <div className="p-4 border-b border-surface-200 dark:border-surface-800">
            <h2 className="font-medium text-surface-900 dark:text-white flex items-center gap-2">
              <CircleStackIcon className="w-5 h-5 text-green-500" />
              Storage Breakdown
            </h2>
          </div>
          <div className="p-4">
            {storage?.breakdown?.length > 0 ? (
              <div className="space-y-4">
                {storage.breakdown.map((item, i) => (
                  <div key={i} className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-surface-100 dark:bg-surface-800 flex items-center justify-center">
                        <ChartBarIcon className="w-4 h-4 text-surface-500" />
                      </div>
                      <div>
                        <p className="font-medium text-surface-900 dark:text-white">{item.name}</p>
                        <p className="text-xs text-surface-500">{item.rows?.toLocaleString()} rows</p>
                      </div>
                    </div>
                    <span className="text-surface-600 dark:text-surface-400">
                      {item.sizeFormatted}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <div className="h-[200px] flex items-center justify-center text-surface-500">
                No data sources yet
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Current Period */}
      <div className="card p-6 mt-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="font-medium text-surface-900 dark:text-white">Current Billing Period</h3>
            <p className="text-sm text-surface-500 mt-1">
              {usage?.currentPeriod?.start && (
                <>
                  {new Date(usage.currentPeriod.start).toLocaleDateString()} - {new Date(usage.currentPeriod.end).toLocaleDateString()}
                </>
              )}
            </p>
          </div>
          <div className="text-right">
            <p className="text-sm text-surface-500">Plan</p>
            <p className="font-medium text-surface-900 dark:text-white capitalize">
              {usage?.plan || 'Free'}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

